import { useMediaQuery, useTheme } from "@mui/material"
import DOMPurify from "dompurify"

import { Drawer } from "src/components/common/drawer"
import { BasicLayoutStyleProps } from "src/components/layouts/basic"
import { create } from "src/helpers/bem"
import { useTranslation } from "src/hooks/translation"
import { Dialogs, mergeDialog, useDialog } from "src/states/dialogs"

import { useCategories } from "src/states/shop/category"
import { useFilter } from "src/states/shop/filter"

import styles from "./ShopOverviewContactDialog.module.scss"

const bem = create(styles, "ShopOverviewContactDialog")

type ShopOverviewContactDialogProps = {
  style: BasicLayoutStyleProps
}

export const ShopOverviewContactDialog = ({
  style,
}: ShopOverviewContactDialogProps) => {
  const theme = useTheme()
  const { open } = useDialog(
    "shopOverviewContactDialog",
  ) as Dialogs["shopOverviewContactDialog"]
  const { filterBy } = useFilter()
  const { categories } = useCategories()
  const { messages: translations } = useTranslation()
  const smallScreen = useMediaQuery(theme.breakpoints.down("xl"))

  const { category } = filterBy ?? {}
  const contactDialogTranslations =
    translations.pages.shop.overview.contactDialog
  const parentCategory = category?.parentCategoryId
    ? categories.find((c) => c.id === category.parentCategoryId)
    : category

  const clearDynamicsHtml = (html: string) => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, "text/html")
    const ckContent = doc.querySelector(".ck-content")

    return DOMPurify.sanitize(ckContent ? ckContent.innerHTML : html)
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      style={style}
      PaperProps={
        smallScreen
          ? {
              sx: {
                width: "30%",
              },
            }
          : {
              sx: {
                width: "20%",
              },
            }
      }
      onClose={() => mergeDialog("shopOverviewContactDialog", { open: false })}
      onOpen={() => mergeDialog("shopOverviewContactDialog", { open: true })}
    >
      <div className={bem()}>
        <h2 className={bem("title")}>{contactDialogTranslations.title}</h2>

        {parentCategory && (
          <div className={bem("information__list")}>
            <div className={bem("section")}>
              <div className={bem("section__title")}>
                {contactDialogTranslations.description}
              </div>
              <div className={bem("section__content")}>
                {parentCategory.notice?.content ?? "--"}
              </div>
            </div>

            <div className={bem("section")}>
              <div className={bem("section__title")}>
                {contactDialogTranslations.orderDeadline}
              </div>
              <div className={bem("section__content")}>
                {parentCategory.orderDeadline ?? "--"}
              </div>
            </div>

            <div className={bem("section")}>
              <div className={bem("section__title")}>
                {contactDialogTranslations.orderDeadlineInfo}
              </div>
              <div className={bem("section__content")}>
                {parentCategory.info ?? "--"}
              </div>
            </div>

            <div className={bem("section")}>
              <div className={bem("section__title")}>
                {contactDialogTranslations.contactPerson}
              </div>
              <div
                className={bem("section__content")}
                dangerouslySetInnerHTML={{
                  __html: parentCategory.contactString
                    ? clearDynamicsHtml(parentCategory.contactString)
                    : "--",
                }}
              />
            </div>
          </div>
        )}
      </div>
    </Drawer>
  )
}

export default ShopOverviewContactDialog
